import constants from "~/constants"
import { SlugTitleType } from "~/types/schema"

export default {
  // Generic methods
  get(key: string) {
    try {
      const value = localStorage.getItem(key)
      if (value) {
        return JSON.parse(value)
      } else {
        return null
      }
    } catch (e) {
      console.log("Storage", key, e)
      return null
    }
  },
  set(key: string, value: any) {
    return localStorage.setItem(key, JSON.stringify(value))
  },

  // Specific & explicit methods
  getAuthToken() {
    return this.get(constants.AUTH_TOKEN_KEY)
  },

  setAuthToken(value: string | null) {
    return this.set(constants.AUTH_TOKEN_KEY, value)
  },

  clearAuthToken() {
    return localStorage.removeItem(constants.AUTH_TOKEN_KEY)
  },

  getAuthModule() {
    return this.get(constants.AUTH_MODULE)
  },

  setAuthModule(value: string) {
    return this.set(constants.AUTH_MODULE, value)
  },

  getCurrentUser() {
    return this.get(constants.USER_CURRENT)
  },

  setCurrentUser(value: Record<string, any> | null) {
    return this.set(constants.USER_CURRENT, value)
  },

  clearCurrentUser() {
    return localStorage.removeItem(constants.USER_CURRENT)
  },

  clearSmartTopicsGradeSelect() {
    return localStorage.removeItem(constants.SMART_TOPICS_GRADES_SELECT)
  },

  getLastPath() {
    return this.get(constants.NAVIGATION_LAST_PATH)
  },

  setLastPath(value: string) {
    return this.set(constants.NAVIGATION_LAST_PATH, value)
  },

  clearLastPath() {
    return localStorage.removeItem(constants.NAVIGATION_LAST_PATH)
  },

  getTeacherFilteredJournals() {
    return this.get(constants.TEACHER_FILTERED_JOURNALS) || []
  },

  setTeacherFilteredJournals(value: string[]) {
    return this.set(constants.TEACHER_FILTERED_JOURNALS, value)
  },

  getStudentFilteredSubjects() {
    return this.get(constants.STUDENT_FILTERED_SUBJECTS) || []
  },

  setStudentFilteredSubjects(value: string[]) {
    return this.set(constants.STUDENT_FILTERED_SUBJECTS, value)
  },

  getTeacherCollapsedJournals() {
    return this.get(constants.TEACHER_COLLAPSED_JOURNALS) || []
  },

  setTeacherCollapsedJournals(value: number[]) {
    return this.set(constants.TEACHER_COLLAPSED_JOURNALS, value)
  },

  getStudentCollapsedLessons() {
    return this.get(constants.STUDENT_COLLAPSED_LESSONS) || []
  },

  setStudentCollapsedLessons(value: number[]) {
    return this.set(constants.STUDENT_COLLAPSED_LESSONS, value)
  },

  setSmartTopicsSubjectGradeFilter(subjectSlug: string, value: any) {
    return this.set(`${constants.SMART_TOPICS_SUBJECT_GRADE_FILTER}:${subjectSlug}`, value)
  },

  getSmartTopicsSubjectGradeFilter(subjectSlug: string) {
    return this.get(`${constants.SMART_TOPICS_SUBJECT_GRADE_FILTER}:${subjectSlug}`) || []
  },

  setProductFilters(productType: string, value: any) {
    return this.set(`${constants.PRODUCT_FILTERS_PREFIX}:${productType}`, value)
  },

  getProductFilters(productType: string) {
    return (
      this.get(`${constants.PRODUCT_FILTERS_PREFIX}:${productType}`) || {
        categories: [] as SlugTitleType[],
        grades: [] as SlugTitleType[],
        subjects: [] as SlugTitleType[],
        curriculumTypes: [] as SlugTitleType[],
      }
    )
  },

  setMyJournalsCollapsed(value: string[]) {
    return this.set(constants.MY_JOURNALS_COLLAPSED, value)
  },

  getMyJournalsCollapsed() {
    return this.get(constants.MY_JOURNALS_COLLAPSED) || []
  },

  setMyJournalsFilters(value: Record<number, string[]>) {
    return this.set(constants.MY_JOURNALS_FILTERS, value)
  },

  getMyJournalsFilters() {
    return this.get(constants.MY_JOURNALS_FILTERS) || {}
  },

  setStudentJournalsCollapsed(value: number[]) {
    return this.set(constants.STUDENT_JOURNALS_COLLAPSED, value)
  },

  getStudentJournalsCollapsed() {
    return this.get(constants.STUDENT_JOURNALS_COLLAPSED) || []
  },

  setStudentJournalsFilters(value: number[]) {
    return this.set(constants.STUDENT_JOURNALS_FILTERS, value)
  },

  getStudentJournalsFilters() {
    return this.get(constants.STUDENT_JOURNALS_FILTERS) || []
  },

  getTeacherCollapsedWorksheetJournalsWithProducts() {
    return this.get(constants.TEACHER_COLLAPSED_WORKSHEET_JOURNALS) || []
  },

  setTeacherCollapsedWorksheetJournalsWithProducts(value: number[]) {
    return this.set(constants.TEACHER_COLLAPSED_WORKSHEET_JOURNALS, value)
  },

  getTeacherFilteredWorksheetJournals() {
    return this.get(constants.TEACHER_FILTERED_WORKSHEET_JOURNALS) || []
  },

  setTeacherFilteredWorksheetJournals(value: string[]) {
    return this.set(constants.TEACHER_FILTERED_WORKSHEET_JOURNALS, value)
  },

  getStudentCollapsedWorksheetJournalsWithProducts() {
    return this.get(constants.STUDENT_COLLAPSED_WORKSHEET_JOURNALS) || []
  },

  setStudentCollapsedWorksheetJournalsWithProducts(value: number[]) {
    return this.set(constants.STUDENT_COLLAPSED_WORKSHEET_JOURNALS, value)
  },

  getStudentFilteredWorksheetSubjects() {
    return this.get(constants.STUDENT_FILTERED_WORKSHEET_SUBJECTS) || []
  },

  setStudentFilteredWorksheetSubjects(value: string[]) {
    return this.set(constants.STUDENT_FILTERED_WORKSHEET_SUBJECTS, value)
  },
}
